import type {
  FrontendApi,
  SettingsFlow,
  UiNodeInputAttributes,
  UpdateSettingsFlowBody,
} from '@ory/kratos-client';
import type { AxiosError } from 'axios';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMount } from 'react-use';

import { oryClient } from '@revelio/auth';

import { dashboardUrl } from '../../wilson-brand';
import { useSdkError } from '../ory/sdk';
import type { PasswordResetFormValuesRequired } from './models';
import { PasswordInput } from './password-input';
import { ThemeHeading, ThemeLink, ThemeTagline } from './theme/theme';
import { Button, Spinner, Stack, uiStyles } from './ui';

// Same Kratos settings-flow plumbing as the legacy page — only the rendering
// changed.
export function SettingsForm() {
  const [flow, setFlow] = useState<SettingsFlow | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<PasswordResetFormValuesRequired>();

  const navigate = useNavigate();
  const sdk = oryClient as FrontendApi;

  // biome-ignore lint/suspicious/noConfusingVoidType: mirrors the legacy page
  // biome-ignore lint/complexity/noUselessUndefinedInitialization: mirrors the legacy page
  let getFlow: ((flowId: string) => Promise<void | AxiosError>) | undefined =
    undefined;
  const sdkErrorHandler = useSdkError(getFlow, setFlow, '/settings', true);
  // biome-ignore lint/correctness/useExhaustiveDependencies: mirrors the legacy page
  getFlow = useCallback(
    (flowId: string) =>
      sdk
        .getSettingsFlow({ id: flowId })
        .then(({ data: flow }) => setFlow(flow))
        .catch(sdkErrorHandler),
    [sdk, sdkErrorHandler]
  );

  const createFlow = () => {
    sdk
      .createBrowserSettingsFlow()
      .then(({ data: flow }) => {
        // Update URI query params to include flow id, keeping the theme pin
        // and redesign override intact.
        setSearchParams((params) => {
          const next = new URLSearchParams(params);
          next.set('flow', flow.id);
          return next;
        });
        setFlow(flow);
      })
      .catch(sdkErrorHandler);
  };

  const onSubmit = (body: UpdateSettingsFlowBody) => {
    if (!flow) {
      return navigate('/settings', { replace: true });
    }

    sdk
      .updateSettingsFlow({ flow: flow.id, updateSettingsFlowBody: body })
      .then(({ data: flow }) => {
        setFlow(flow);
      })
      .catch(sdkErrorHandler);
  };

  useMount(() => {
    const flowId = searchParams.get('flow');
    if (flowId) {
      getFlow && getFlow(flowId).catch(createFlow);
      return;
    }
    createFlow();
  });

  // 1050001 = "Your changes have been saved!" — the only cue Kratos gives that
  // the password actually changed.
  const PASSWORD_CHANGED_MESSAGE_ID = 1050001;

  const onSubmitForm = handleSubmit((formData: { password: string }) => {
    if (!flow) return;

    const csrfToken = (
      flow.ui.nodes.find(
        (node) =>
          node.attributes.node_type === 'input' &&
          node.attributes.name === 'csrf_token'
      )?.attributes as UiNodeInputAttributes
    )?.value;

    if (!csrfToken) {
      console.error('No CSRF token found');
      return;
    }

    onSubmit({
      csrf_token: csrfToken,
      password: formData.password,
      method: 'password',
    });
  });

  if (!flow) {
    return (
      <div className={uiStyles.centerBlock}>
        <Spinner className={uiStyles.mutedSpinner} />
      </div>
    );
  }

  return (
    <Stack gap="var(--ap-cta-gap)" fullWidth>
      <Stack gap="12px">
        <ThemeHeading>Change your password</ThemeHeading>
        <ThemeTagline>
          Choose a new password for your Revelio Labs account
        </ThemeTagline>
      </Stack>

      <form onSubmit={onSubmitForm}>
        <Stack gap="16px">
          <PasswordInput register={register} watch={watch} errors={errors} />
          <Button style={{ marginTop: '8px' }} type="submit">
            Update password
          </Button>
        </Stack>
      </form>

      {flow.ui.messages?.map((message) => (
        <div key={message.id} className={uiStyles.messageRow}>
          <p
            className={`${uiStyles.message}${
              message.type === 'error' ? ` ${uiStyles.messageError}` : ''
            }`}
          >
            {message.text}
          </p>
          {message.type === 'success' &&
            message.id === PASSWORD_CHANGED_MESSAGE_ID && (
              <ThemeLink
                href={`${dashboardUrl}/company/compositions`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Go to Dashboard
              </ThemeLink>
            )}
        </div>
      ))}
    </Stack>
  );
}
